import { useCallback, useEffect, useState } from 'react';
import {
  WEBHOOK_EVENT_NAMES,
  type WebhookDelivery,
  type WebhookDeliveryDetail,
  type WebhookDeliveryStats,
  type WebhookDeliveryStatus,
  type WebhookIntegration,
} from '@supporchat/shared';
import { api } from '../lib/api';

/**
 * Acompanhamento das entregas ao sistema existente.
 *
 * O envio é assíncrono e com retentativa, o que é bom para o atendimento e ruim
 * para quem precisa confiar nele: sem esta tela, "o atendimento não apareceu no
 * CRM" só se responde lendo o banco. Aqui aparecem o que foi enviado, o que o
 * outro lado respondeu e o botão que reenvia.
 *
 * Recarrega sozinha porque nada aqui chega por WebSocket: a entrega acontece no
 * worker, não no ciclo de uma requisição do painel.
 */
const REFRESH_MS = 20_000;
const PAGE_SIZE = 25;

const STATUS_FILTERS: Array<{ id: WebhookDeliveryStatus | ''; label: string }> = [
  { id: '', label: 'Todas' },
  { id: 'failed', label: 'Falharam' },
  { id: 'discarded', label: 'Recusadas' },
  { id: 'pending', label: 'Na fila' },
  { id: 'delivered', label: 'Entregues' },
];

const STATUS_LABEL: Record<WebhookDeliveryStatus, string> = {
  pending: 'na fila',
  delivered: 'entregue',
  failed: 'falhou',
  discarded: 'recusada',
};

/**
 * `failed` é vermelho (o outro lado caiu, vale insistir) e `discarded` é âmbar:
 * o receptor respondeu 4xx recusando o conteúdo, então quem precisa agir é gente,
 * não a fila.
 */
const STATUS_BADGE: Record<WebhookDeliveryStatus, string> = {
  pending: 'badge--bot',
  delivered: 'badge--ready',
  failed: 'badge--failed',
  discarded: 'badge--pending',
};

const EVENT_LABEL: Record<string, string> = {
  'atendimento.criado': 'Atendimento aberto',
  'atendimento.atualizado': 'Atendimento atualizado',
  'atendimento.encerrado': 'Atendimento encerrado',
  'interacao.criada': 'Mensagem na timeline',
  'interacao.atualizada': 'Mensagem atualizada',
  ping: 'Teste de integração',
};

export function WebhooksPage() {
  const [config, setConfig] = useState<WebhookIntegration | null>(null);
  const [stats, setStats] = useState<WebhookDeliveryStats | null>(null);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<WebhookDeliveryStatus | ''>('');
  const [event, setEvent] = useState('');
  const [detail, setDetail] = useState<WebhookDeliveryDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [integration, counts, list] = await Promise.all([
        api.webhookConfig(),
        api.webhookStats(),
        api.listWebhookDeliveries({
          status: status || undefined,
          event: event || undefined,
          page,
          pageSize: PAGE_SIZE,
        }),
      ]);
      setConfig(integration);
      setStats(counts);
      setDeliveries(list.items);
      setTotal(list.total);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [status, event, page]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const timer = window.setInterval(() => void load(), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  /** Envolve as ações: uma só trava, e o resultado sempre recarrega a tela. */
  const run = async (action: () => Promise<string>) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setNotice(await action());
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const open = async (id: string) => {
    setError(null);
    try {
      setDetail(await api.getWebhookDelivery(id));
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const pendingFix = (stats?.failed ?? 0) + (stats?.discarded ?? 0);
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="page">
      <div className="page__inner">
        {error && <div className="alert alert--error">{error}</div>}
        {notice && <div className="alert alert--success">{notice}</div>}

        {config && !config.enabled && (
          <div className="alert alert--info">
            Integração desligada: nada é enviado ao sistema existente. Configure{' '}
            <code className="mono">WEBHOOK_URL</code> e{' '}
            <code className="mono">WEBHOOK_ENABLED=true</code> no servidor. O envio de teste funciona
            antes disso, para conferir a rota.
          </div>
        )}
        {config?.enabled && !config.hasSecret && (
          <div className="alert alert--error">
            Sem <code className="mono">WEBHOOK_SECRET</code>: as entregas saem assinadas com segredo
            vazio, e qualquer um consegue forjar um atendimento no sistema existente.
          </div>
        )}

        <div className="card">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <h3 className="card__title">Integração com o sistema existente</h3>
            <span
              className={`badge ${config?.enabled ? 'badge--ready' : 'badge--closed'}`}
              style={{ marginLeft: 'auto' }}
            >
              {config?.enabled ? 'ligada' : 'desligada'}
            </span>
          </div>
          <p className="card__subtitle">
            Cada conversa daqui vira um atendimento lá, e cada mensagem uma interação na timeline dele.
          </p>

          {config && (
            <>
              <table className="table">
                <tbody>
                  <tr>
                    <th style={{ width: 190 }}>Endereço</th>
                    <td className="mono-cell">
                      {config.url ?? <span className="muted">nenhum configurado</span>}
                    </td>
                  </tr>
                  <tr>
                    <th>Assinatura</th>
                    <td>
                      {config.hasSecret ? (
                        'HMAC-SHA256 com segredo configurado'
                      ) : (
                        <span className="muted">sem segredo</span>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th>Eventos enviados</th>
                    <td className="mono-cell">{config.events.join(', ')}</td>
                  </tr>
                  <tr>
                    <th>Tentativas por evento</th>
                    <td>
                      {config.maxAttempts} (backoff exponencial), tempo limite de{' '}
                      {Math.round(config.timeoutMs / 1000)}s
                    </td>
                  </tr>
                  <tr>
                    <th>Id do atendimento</th>
                    <td className="mono-cell">
                      {config.ticketIdPath ?? <span className="muted">não é lido da resposta</span>}
                    </td>
                  </tr>
                  <tr>
                    <th>Retenção</th>
                    <td>
                      {config.retentionDays === 0
                        ? 'entregas nunca são apagadas'
                        : `entregues são apagadas após ${config.retentionDays} dias; falhas ficam`}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div className="btn-row">
                <button
                  className="btn btn--primary"
                  disabled={busy || !config.url}
                  onClick={() =>
                    void run(async () => {
                      const delivery = await api.testWebhook();
                      return `Teste enviado. Acompanhe a entrega ${delivery.id.slice(0, 8)} na lista abaixo.`;
                    })
                  }
                >
                  Enviar teste
                </button>
                <button className="btn" disabled={busy} onClick={() => void load()}>
                  Atualizar
                </button>
              </div>
              <span className="field__hint">
                O teste envia um evento <code className="mono">ping</code> mesmo com a integração
                desligada — é como conferir endereço, segredo e rota antes de ligar.
              </span>
            </>
          )}
        </div>

        {stats && (
          <div className="card">
            <div className="stat-row">
              <div className="stat">
                <div className="stat__value">{stats.delivered}</div>
                <div className="stat__label">entregues</div>
              </div>
              <div className="stat">
                <div className="stat__value">{stats.pending}</div>
                <div className="stat__label">na fila</div>
              </div>
              <div className="stat">
                <div className="stat__value">{stats.failed}</div>
                <div className="stat__label">falharam</div>
              </div>
              <div className="stat">
                <div className="stat__value">{stats.discarded}</div>
                <div className="stat__label">recusadas</div>
              </div>
            </div>

            <p className="field__hint" style={{ marginTop: 10 }}>
              {stats.lastDeliveredAt
                ? `Última entrega: ${formatWhen(stats.lastDeliveredAt)}.`
                : 'Nenhuma entrega concluída ainda.'}
              {stats.lastFailureAt && ` Última falha: ${formatWhen(stats.lastFailureAt)}.`}
            </p>

            {pendingFix > 0 && (
              <div className="btn-row">
                <button
                  className="btn btn--primary"
                  disabled={busy}
                  onClick={() =>
                    void run(async () => {
                      const { queued } = await api.retryFailedWebhooks();
                      return `${queued} entrega(s) recolocada(s) na fila.`;
                    })
                  }
                >
                  Reenviar as {pendingFix} que pararam
                </button>
                <span className="field__hint">
                  Da mais antiga para a mais nova. O receptor deve ser idempotente — reenvio repete o
                  corpo original, não cria atendimento novo.
                </span>
              </div>
            )}
          </div>
        )}

        <div className="card">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <h3 className="card__title">Entregas</h3>
            <span className="muted" style={{ marginLeft: 'auto', fontSize: 12 }}>
              atualiza sozinho a cada {REFRESH_MS / 1000}s
            </span>
          </div>

          <div className="conversation-list__filters" style={{ border: 0, padding: '0 0 12px' }}>
            {STATUS_FILTERS.map((item) => (
              <button
                key={item.id || 'all'}
                className="filter-chip"
                aria-pressed={status === item.id}
                onClick={() => {
                  setStatus(item.id);
                  setPage(1);
                }}
              >
                {item.label}
                {item.id === 'failed' && (stats?.failed ?? 0) > 0 && ` (${stats?.failed})`}
                {item.id === 'discarded' && (stats?.discarded ?? 0) > 0 && ` (${stats?.discarded})`}
              </button>
            ))}

            <select
              value={event}
              style={{ marginLeft: 'auto' }}
              onChange={(e) => {
                setEvent(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Todos os eventos</option>
              {WEBHOOK_EVENT_NAMES.map((name) => (
                <option key={name} value={name}>
                  {EVENT_LABEL[name] ?? name}
                </option>
              ))}
            </select>
          </div>

          {detail && (
            <DeliveryDetail
              delivery={detail}
              busy={busy}
              onClose={() => setDetail(null)}
              onRetry={() =>
                void run(async () => {
                  await api.retryWebhookDelivery(detail.id);
                  await open(detail.id);
                  return 'Entrega recolocada na fila.';
                })
              }
              onResync={() =>
                void run(async () => {
                  const { queued } = await api.resyncConversationWebhooks(detail.conversationId!);
                  return `${queued} evento(s) da conversa recolocado(s) na fila.`;
                })
              }
            />
          )}

          {loading ? (
            <p className="muted">Carregando...</p>
          ) : deliveries.length === 0 ? (
            <p className="muted">
              {status || event
                ? 'Nenhuma entrega com esse filtro.'
                : 'Nenhuma entrega registrada ainda. Com a integração ligada, a próxima mensagem de cliente gera a primeira.'}
            </p>
          ) : (
            <>
              <table className="table">
                <thead>
                  <tr>
                    <th>Quando</th>
                    <th>Evento</th>
                    <th>Situação</th>
                    <th>Tentativas</th>
                    <th>Resposta</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {deliveries.map((delivery) => (
                    <tr
                      key={delivery.id}
                      className={delivery.status === 'failed' ? 'table__row--alert' : undefined}
                    >
                      <td style={{ whiteSpace: 'nowrap' }}>{formatWhen(delivery.createdAt)}</td>
                      <td>
                        <div>{EVENT_LABEL[delivery.event] ?? delivery.event}</div>
                        <div className="muted mono" style={{ fontSize: 11 }}>
                          {delivery.event}
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${STATUS_BADGE[delivery.status]}`}>
                          {STATUS_LABEL[delivery.status]}
                        </span>
                      </td>
                      <td className="mono-cell">{delivery.attempts}</td>
                      <td>
                        {delivery.responseStatus ? (
                          <span className="mono">HTTP {delivery.responseStatus}</span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                        {extraError(delivery) && (
                          <div className="muted" style={{ fontSize: 11 }}>
                            {extraError(delivery)}
                          </div>
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button className="btn btn--sm" onClick={() => void open(delivery.id)}>
                          Ver
                        </button>
                        {(delivery.status === 'failed' || delivery.status === 'discarded') && (
                          <button
                            className="btn btn--sm"
                            style={{ marginLeft: 6 }}
                            disabled={busy}
                            onClick={() =>
                              void run(async () => {
                                await api.retryWebhookDelivery(delivery.id);
                                return 'Entrega recolocada na fila.';
                              })
                            }
                          >
                            Reenviar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="btn-row">
                <button className="btn btn--sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  Anteriores
                </button>
                <button
                  className="btn btn--sm"
                  disabled={page >= lastPage}
                  onClick={() => setPage(page + 1)}
                >
                  Próximas
                </button>
                <span className="field__hint">
                  {total === 0
                    ? 'nenhuma entrega'
                    : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} de ${total}`}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * O corpo enviado e a resposta recebida.
 *
 * O payload é mostrado como JSON cru de propósito: é exatamente o que o outro
 * lado recebeu, e é isso que se cola numa conversa com quem cuida do CRM. Formatá-lo
 * em campos escondería a diferença entre "não mandamos" e "mandamos nulo".
 */
function DeliveryDetail({
  delivery,
  busy,
  onClose,
  onRetry,
  onResync,
}: {
  delivery: WebhookDeliveryDetail;
  busy: boolean;
  onClose: () => void;
  onRetry: () => void;
  onResync: () => void;
}) {
  return (
    <div className="delivery-detail">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
        <strong>{EVENT_LABEL[delivery.event] ?? delivery.event}</strong>
        <span className={`badge ${STATUS_BADGE[delivery.status]}`}>
          {STATUS_LABEL[delivery.status]}
        </span>
        <button className="btn btn--sm" style={{ marginLeft: 'auto' }} onClick={onClose}>
          Fechar
        </button>
      </div>

      <table className="table" style={{ marginBottom: 10 }}>
        <tbody>
          <tr>
            <th style={{ width: 190 }}>Entrega</th>
            <td className="mono-cell">{delivery.id}</td>
          </tr>
          <tr>
            <th>Destino</th>
            <td className="mono-cell">{delivery.endpointUrl}</td>
          </tr>
          <tr>
            <th>Criada</th>
            <td>{formatWhen(delivery.createdAt)}</td>
          </tr>
          <tr>
            <th>Entregue</th>
            <td>
              {delivery.deliveredAt ? formatWhen(delivery.deliveredAt) : <span className="muted">—</span>}
            </td>
          </tr>
          {delivery.lastError && (
            <tr>
              <th>Último erro</th>
              <td>{delivery.lastError}</td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="field">
        <label>Corpo enviado (assinado)</label>
        <pre className="code-view">{JSON.stringify(delivery.payload, null, 2)}</pre>
      </div>

      <div className="field">
        <label>Resposta do sistema existente</label>
        <pre className="code-view">
          {delivery.responseStatus ? `HTTP ${delivery.responseStatus}\n\n` : ''}
          {delivery.responseBody ?? '(sem corpo)'}
        </pre>
      </div>

      <div className="btn-row">
        {delivery.status !== 'pending' && (
          <button className="btn" disabled={busy} onClick={onRetry}>
            Reenviar esta entrega
          </button>
        )}
        {delivery.conversationId && (
          <button
            className="btn"
            disabled={busy}
            onClick={() => {
              if (
                window.confirm(
                  'Reenviar a conversa inteira: abertura, cada mensagem e o encerramento. Use quando o atendimento não existe no outro lado.',
                )
              ) {
                onResync();
              }
            }}
          >
            Reenviar a conversa inteira
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * O erro só aparece quando diz algo que a coluna de resposta não disse.
 *
 * Recusa do receptor grava `HTTP 422` nos dois campos, e repetir o mesmo texto
 * lado a lado faz o operador procurar diferença onde não há. Falha de rede, que é
 * o caso em que o erro é a única informação, continua visível.
 */
function extraError(delivery: WebhookDelivery): string | null {
  if (!delivery.lastError) return null;
  return delivery.lastError === `HTTP ${delivery.responseStatus}` ? null : delivery.lastError;
}

function formatWhen(iso: string): string {
  const date = new Date(iso);
  const mesmoDia = date.toDateString() === new Date().toDateString();

  return mesmoDia
    ? `hoje ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`
    : date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
}
